import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Square,
  Check,
  Dumbbell,
  Clock,
  Timer,
  Search,
  X,
} from 'lucide-react';
import { workoutsApi, exercisesApi } from '@/lib/api';
import { saveDraft, clearDraft } from '@/lib/liveDraft';
import { formatTimer } from '@/lib/restTimer';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ExercisePickerModal } from '@/components/ExercisePickerModal';
import { MobileNumberStepper } from '@/components/MobileNumberStepper';
import { CompactSetTable } from '@/components/CompactSetTable';
import { RestTimer } from '@/components/RestTimer';
import { WorkoutSummary } from '@/components/WorkoutSummary';
import { cn } from '@/lib/utils';
import type { Exercise, MuscleGroup, Workout, WorkoutExercise } from '@/types';

function formatElapsedTimer(startedAt: string): string {
  const diff = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export function LiveWorkoutPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // UI state
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [showExercisePicker, setShowExercisePicker] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [restTargetEnd, setRestTargetEnd] = useState<number | null>(null);
  const [setSavedFeedback, setSetSavedFeedback] = useState(false);
  const [showInlineSearch, setShowInlineSearch] = useState(false);
  const [inlineSearch, setInlineSearch] = useState('');

  // New set input
  const [newSetWeight, setNewSetWeight] = useState<number | null>(20);
  const [newSetReps, setNewSetReps] = useState(8);

  // Globaali lepoajan oletus localStoragesta (osio 6.4, avain
  // treeni.defaultRestSeconds; tuetaan vanha avain restTimerDuration). Toimii
  // fallbackina kun liikkeelle ei ole omaa asetusta.
  const [restDuration] = useState(() => {
    try {
      const stored =
        localStorage.getItem('treeni.defaultRestSeconds') ??
        localStorage.getItem('restTimerDuration');
      return stored ? Number(stored) : 90;
    } catch {
      return 90;
    }
  });

  // Recent exercises from localStorage
  const [recentExerciseIds, setRecentExerciseIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('recentExerciseIds');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Elapsed timer
  const [elapsed, setElapsed] = useState('00:00');

  // Fetch workout
  const { data: workout, isLoading: loadingWorkout } = useQuery({
    queryKey: ['workout', id],
    queryFn: () => workoutsApi.get(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  // Fetch exercises list (for picker)
  const { data: exercisesData } = useQuery({
    queryKey: ['exercises'],
    queryFn: () => exercisesApi.list(),
  });

  const exercises = workout?.exercises || [];
  const currentExercise: WorkoutExercise | undefined = exercises[currentExerciseIndex];
  const allExercisesList = exercisesData?.exercises || [];

  // Viimeisimmät liikkeet pikanapoiksi
  const recentExercises = recentExerciseIds
    .map((eid) => allExercisesList.find((e) => e.id === eid))
    .filter((e): e is Exercise => !!e)
    .slice(0, 5);

  // Inline-haun tulokset
  const filteredExercises = inlineSearch.length >= 1
    ? allExercisesList.filter((e) =>
        e.name.toLowerCase().includes(inlineSearch.toLowerCase())
      )
    : [];

  // Elapsed timer interval
  useEffect(() => {
    if (!workout?.startedAt || showSummary) return;
    setElapsed(formatElapsedTimer(workout.startedAt));
    const interval = setInterval(() => {
      setElapsed(formatElapsedTimer(workout.startedAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [workout?.startedAt, showSummary]);

  // beforeunload warning
  useEffect(() => {
    if (showSummary || workout?.completedAt) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [showSummary, workout?.completedAt]);

  // Osoitin keskeneräiseen live-treeniin (osio 6.5) → etusivun "Jatka"-banneri.
  // Sarjat ovat jo palvelimella; osoitin kertoo mihin treeniin palataan.
  useEffect(() => {
    if (!workout) return;
    if (workout.completedAt || showSummary) {
      clearDraft();
      return;
    }
    saveDraft({
      workoutId: workout.id,
      name: workout.name ?? null,
      startedAt: workout.startedAt,
    });
  }, [workout?.id, workout?.completedAt, workout?.name, workout?.startedAt, showSummary]);

  // Edellisen kerran sarjat (ghost-esitäyttö + PR-korostus) ja liikekohtainen
  // lepoaika. Molemmat user-scoped ja liikekohtaisia (osio 6.2 & 6.4).
  const previousSetsQuery = useQuery({
    queryKey: ['previousSets', currentExercise?.exerciseId],
    queryFn: () => exercisesApi.getPreviousSets(currentExercise!.exerciseId),
    enabled: !!currentExercise?.exerciseId,
  });

  const settingsQuery = useQuery({
    queryKey: ['exerciseSettings', currentExercise?.exerciseId],
    queryFn: () => exercisesApi.getSettings(currentExercise!.exerciseId),
    enabled: !!currentExercise?.exerciseId,
  });

  const previousSets = previousSetsQuery.data?.sets ?? [];
  const perExerciseRest = settingsQuery.data?.restSeconds ?? null;
  const effectiveRest = perExerciseRest ?? restDuration;

  // Edellisen kerran paras paino → PR-korostus taulukossa
  const weightedPrev = previousSets
    .map((s) => s.weight)
    .filter((w): w is number => w !== null);
  const previousBestWeight = weightedPrev.length ? Math.max(...weightedPrev) : null;

  // Ghost-arvo seuraavalle sarjalle: saman indeksin edellinen sarja, muuten viimeisin
  const nextSetIndex = currentExercise ? currentExercise.sets.length : 0;
  const ghostForNext =
    previousSets[nextSetIndex] ?? previousSets[previousSets.length - 1] ?? null;
  const ghostText =
    ghostForNext && ghostForNext.weight !== null
      ? `${ghostForNext.weight} kg × ${ghostForNext.reps ?? '-'}`
      : ghostForNext && ghostForNext.reps !== null
        ? `${ghostForNext.reps} toistoa`
        : null;

  const prefillFromExercise = useCallback(
    (we: WorkoutExercise | undefined) => {
      if (!we) return;
      if (we.sets.length > 0) {
        const last = we.sets.at(-1);
        if (last) {
          // Prisma Decimal tulee API:sta merkkijonona — varmistetaan number-tyyppi
          setNewSetWeight(last.weight !== null ? Number(last.weight) : null);
          setNewSetReps(Number(last.reps) || 8);
        }
      } else if (previousSetsQuery.data?.sets?.length) {
        const first = previousSetsQuery.data.sets[0];
        if (first) {
          setNewSetWeight(first.weight !== null ? Number(first.weight) : null);
          setNewSetReps(Number(first.reps) || 8);
        }
      }
    },
    [previousSetsQuery.data]
  );

  // Update prefill when exercise changes
  useEffect(() => {
    prefillFromExercise(currentExercise);
  }, [currentExerciseIndex, currentExercise?.sets.length, prefillFromExercise]);

  // Save recent exercise
  const saveRecentExercise = (exerciseId: string) => {
    const updated = [exerciseId, ...recentExerciseIds.filter((eid) => eid !== exerciseId)].slice(0, 10);
    setRecentExerciseIds(updated);
    try {
      localStorage.setItem('recentExerciseIds', JSON.stringify(updated));
    } catch { /* ignore */ }
  };

  // --- Mutations ---

  const addExerciseMutation = useMutation({
    mutationFn: (exerciseId: string) => workoutsApi.addExercise(id!, exerciseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workout', id] });
    },
  });

  const addSetMutation = useMutation({
    mutationFn: (params: { workoutExerciseId: string; setNumber: number; reps: number; weight?: number }) =>
      workoutsApi.addSet(id!, params.workoutExerciseId, {
        setNumber: params.setNumber,
        reps: params.reps,
        weight: params.weight,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workout', id] });
      // Käynnistä lepoajastin aikaleimalla (osio 6.4)
      setRestTargetEnd(Date.now() + effectiveRest * 1000);
      // Haptic feedback
      if (navigator.vibrate) navigator.vibrate(50);
      // Flash "saved" feedback
      setSetSavedFeedback(true);
      setTimeout(() => setSetSavedFeedback(false), 1500);
    },
  });

  const deleteSetMutation = useMutation({
    mutationFn: (params: { workoutExerciseId: string; setId: string }) =>
      workoutsApi.deleteSet(id!, params.workoutExerciseId, params.setId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workout', id] });
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => workoutsApi.complete(id!),
    onSuccess: (updatedWorkout) => {
      queryClient.setQueryData(['workout', id], updatedWorkout);
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      clearDraft(); // treeni valmis → poista keskeneräisen osoitin (osio 6.5)
      setShowSummary(true);
    },
  });

  // Tallenna liikekohtainen lepoaika (osio 6.4)
  const settingsMutation = useMutation({
    mutationFn: (seconds: number) =>
      exercisesApi.putSettings(currentExercise!.exerciseId, seconds),
    onSuccess: (_data, seconds) => {
      if (currentExercise) {
        queryClient.setQueryData(['exerciseSettings', currentExercise.exerciseId], {
          restSeconds: seconds,
        });
      }
    },
  });

  const createExerciseMutation = useMutation({
    mutationFn: (data: { name: string; muscleGroup?: MuscleGroup }) =>
      exercisesApi.create(data),
    onSuccess: (newExercise) => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] });
      handleSelectExercise(newExercise);
      setShowExercisePicker(false);
    },
  });

  const removeExerciseMutation = useMutation({
    mutationFn: (workoutExerciseId: string) =>
      workoutsApi.removeExercise(id!, workoutExerciseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workout', id] });
      // Adjust index if needed
      if (currentExerciseIndex >= exercises.length - 1 && currentExerciseIndex > 0) {
        setCurrentExerciseIndex(currentExerciseIndex - 1);
      }
    },
  });

  // --- Handlers ---

  const handleSelectExercise = async (exercise: Exercise) => {
    saveRecentExercise(exercise.id);
    await addExerciseMutation.mutateAsync(exercise.id);
    // Navigate to newly added exercise (will be last)
    // We need to refetch first, so set to length (it'll be valid after refetch)
    setCurrentExerciseIndex(exercises.length);
    setShowExercisePicker(false);
    setNewSetWeight(20);
    setNewSetReps(8);
  };

  const handleCreateExercise = (name: string, muscleGroup?: MuscleGroup) => {
    createExerciseMutation.mutate({ name, muscleGroup });
  };

  const handleConfirmSet = () => {
    if (!currentExercise) return;
    const setNumber = currentExercise.sets.length + 1;
    addSetMutation.mutate({
      workoutExerciseId: currentExercise.id,
      setNumber,
      reps: Number(newSetReps),
      weight: newSetWeight !== null ? Number(newSetWeight) : undefined,
    });
  };

  const handleDeleteSet = (setId: string) => {
    if (!currentExercise) return;
    deleteSetMutation.mutate({
      workoutExerciseId: currentExercise.id,
      setId,
    });
  };

  const handleEndWorkout = () => {
    if (confirm('Lopetetaanko treeni?')) {
      completeMutation.mutate();
    }
  };

  const handleRemoveExercise = () => {
    if (!currentExercise) return;
    if (confirm(`Poistetaanko ${currentExercise.exercise?.name || 'liike'} treenistä?`)) {
      removeExerciseMutation.mutate(currentExercise.id);
    }
  };

  // --- Render ---

  if (loadingWorkout) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">Treeniä ei löytynyt</p>
        <button onClick={() => navigate('/')} className="btn btn-secondary">
          Takaisin etusivulle
        </button>
      </div>
    );
  }

  // Show summary after completion
  if (showSummary || workout.completedAt) {
    return <WorkoutSummary workout={workout} />;
  }

  const totalSets = exercises.reduce((sum, ex) => sum + ex.sets.length, 0);

  return (
    <div className="max-w-lg mx-auto space-y-4 animate-fade-in">
      {/* Header: Timer + End workout */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary-500" />
          <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">
            {elapsed}
          </span>
        </div>
        <button
          type="button"
          onClick={handleEndWorkout}
          disabled={completeMutation.isPending}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold',
            'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
            'transition-all touch-manipulation',
            'hover:bg-red-200 dark:hover:bg-red-900/50',
            'active:scale-95',
            'disabled:opacity-50'
          )}
        >
          <Square className="h-4 w-4" />
          {completeMutation.isPending ? 'Lopetetaan...' : 'Lopeta treeni'}
        </button>
      </div>

      {/* Progress bar */}
      {exercises.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{exercises.length} {exercises.length === 1 ? 'liike' : 'liikettä'}</span>
            <span>{totalSets} {totalSets === 1 ? 'sarja' : 'sarjaa'}</span>
          </div>
          <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.min(100, totalSets * 5)}%` }}
            />
          </div>
        </div>
      )}

      {/* Exercise Navigator */}
      {exercises.length === 0 ? (
        // Empty state — show quick picks + search inline
        <div className="card p-5 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
            Lisää ensimmäinen liike
          </h2>

          {/* Viimeisimmät liikkeet */}
          {recentExercises.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Viimeisimmät
              </p>
              <div className="flex flex-wrap gap-2">
                {recentExercises.map((ex) => (
                  <button
                    key={ex.id}
                    type="button"
                    onClick={() => handleSelectExercise(ex)}
                    disabled={addExerciseMutation.isPending}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm font-medium',
                      'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300',
                      'transition-colors touch-manipulation',
                      'hover:bg-primary-100 dark:hover:bg-primary-900/40',
                      'active:scale-[0.97]',
                      'disabled:opacity-50'
                    )}
                  >
                    {ex.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Inline-haku */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={inlineSearch}
              onChange={(e) => setInlineSearch(e.target.value)}
              placeholder="Hae liikettä..."
              className={cn(
                'w-full pl-9 pr-8 py-2.5 rounded-lg text-sm',
                'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white',
                'placeholder-gray-400 dark:placeholder-gray-500',
                'border border-gray-200 dark:border-gray-700',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent'
              )}
            />
            {inlineSearch && (
              <button
                type="button"
                onClick={() => setInlineSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Hakutulokset */}
          {inlineSearch.length >= 1 && (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredExercises.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">
                  Ei tuloksia
                </p>
              ) : (
                filteredExercises.slice(0, 8).map((ex) => (
                  <button
                    key={ex.id}
                    type="button"
                    onClick={() => {
                      handleSelectExercise(ex);
                      setInlineSearch('');
                    }}
                    disabled={addExerciseMutation.isPending}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-lg text-sm',
                      'text-gray-900 dark:text-white',
                      'transition-colors touch-manipulation',
                      'hover:bg-gray-100 dark:hover:bg-gray-800',
                      'active:bg-gray-200 dark:active:bg-gray-700',
                      'disabled:opacity-50'
                    )}
                  >
                    {ex.name}
                    {ex.muscleGroup && (
                      <span className="ml-2 text-xs text-gray-400">
                        {ex.muscleGroup}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {/* Avaa koko modaali */}
          {!inlineSearch && (
            <button
              type="button"
              onClick={() => setShowExercisePicker(true)}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium',
                'text-gray-500 dark:text-gray-400',
                'transition-colors touch-manipulation',
                'hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
            >
              <Plus className="h-4 w-4" />
              Selaa kaikkia liikkeitä
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Exercise header with navigation */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-1">
              <button
                type="button"
                onClick={() => setCurrentExerciseIndex(Math.max(0, currentExerciseIndex - 1))}
                disabled={currentExerciseIndex === 0}
                className={cn(
                  'p-2 rounded-lg transition-colors touch-manipulation',
                  'hover:bg-gray-100 dark:hover:bg-gray-800',
                  'active:bg-gray-200 dark:active:bg-gray-700',
                  'disabled:opacity-30 disabled:pointer-events-none'
                )}
                aria-label="Edellinen liike"
              >
                <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>

              <div className="text-center flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                  {currentExercise?.exercise?.name || 'Liike'}
                </h2>
                <p className="text-sm text-gray-500">
                  {currentExerciseIndex + 1} / {exercises.length}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setCurrentExerciseIndex(Math.min(exercises.length - 1, currentExerciseIndex + 1))
                }
                disabled={currentExerciseIndex >= exercises.length - 1}
                className={cn(
                  'p-2 rounded-lg transition-colors touch-manipulation',
                  'hover:bg-gray-100 dark:hover:bg-gray-800',
                  'active:bg-gray-200 dark:active:bg-gray-700',
                  'disabled:opacity-30 disabled:pointer-events-none'
                )}
                aria-label="Seuraava liike"
              >
                <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setShowInlineSearch(!showInlineSearch)}
                disabled={addExerciseMutation.isPending}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium',
                  'transition-colors touch-manipulation',
                  'active:scale-[0.98]',
                  'disabled:opacity-50',
                  showInlineSearch
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200'
                    : 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40'
                )}
              >
                <Plus className="h-4 w-4" />
                Lisää liike
              </button>
              {currentExercise && currentExercise.sets.length === 0 && (
                <button
                  type="button"
                  onClick={handleRemoveExercise}
                  disabled={removeExerciseMutation.isPending}
                  className={cn(
                    'px-3 py-2 rounded-lg text-sm font-medium',
                    'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
                    'transition-colors touch-manipulation',
                    'hover:bg-red-100 dark:hover:bg-red-900/40',
                    'active:scale-[0.98]',
                    'disabled:opacity-50'
                  )}
                >
                  Poista
                </button>
              )}
            </div>

            {/* Lepoaika tälle liikkeelle (osio 6.4) — tallentuu palvelimelle */}
            {currentExercise && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <Timer className="h-4 w-4 text-gray-400 shrink-0" aria-hidden="true" />
                <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Lepoaika</span>
                <div className="flex gap-1 flex-1 justify-end">
                  {[60, 90, 120, 180].map((sec) => (
                    <button
                      key={sec}
                      type="button"
                      onClick={() => settingsMutation.mutate(sec)}
                      disabled={settingsMutation.isPending}
                      className={cn(
                        'px-2.5 py-1 rounded-lg text-xs font-medium min-h-[32px] touch-manipulation transition-colors',
                        'disabled:opacity-50',
                        effectiveRest === sec
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      )}
                      aria-label={`Aseta lepoajaksi ${sec} sekuntia`}
                      aria-pressed={effectiveRest === sec}
                    >
                      {formatTimer(sec)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Inline exercise picker (expanded) */}
            {showInlineSearch && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-3">
                {/* Viimeisimmät liikkeet */}
                {recentExercises.length > 0 && !inlineSearch && (
                  <div className="flex flex-wrap gap-2">
                    {recentExercises.map((ex) => (
                      <button
                        key={ex.id}
                        type="button"
                        onClick={() => {
                          handleSelectExercise(ex);
                          setShowInlineSearch(false);
                          setInlineSearch('');
                        }}
                        disabled={addExerciseMutation.isPending}
                        className={cn(
                          'px-3 py-1.5 rounded-lg text-sm font-medium',
                          'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
                          'transition-colors touch-manipulation',
                          'hover:bg-gray-200 dark:hover:bg-gray-700',
                          'active:scale-[0.97]',
                          'disabled:opacity-50'
                        )}
                      >
                        {ex.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Hakukenttä */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={inlineSearch}
                    onChange={(e) => setInlineSearch(e.target.value)}
                    placeholder="Hae liikettä..."
                    autoFocus
                    className={cn(
                      'w-full pl-9 pr-8 py-2 rounded-lg text-sm',
                      'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white',
                      'placeholder-gray-400 dark:placeholder-gray-500',
                      'border border-gray-200 dark:border-gray-700',
                      'focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                    )}
                  />
                  {inlineSearch && (
                    <button
                      type="button"
                      onClick={() => setInlineSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Hakutulokset */}
                {inlineSearch.length >= 1 && (
                  <div className="max-h-40 overflow-y-auto space-y-0.5">
                    {filteredExercises.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-2">Ei tuloksia</p>
                    ) : (
                      filteredExercises.slice(0, 8).map((ex) => (
                        <button
                          key={ex.id}
                          type="button"
                          onClick={() => {
                            handleSelectExercise(ex);
                            setShowInlineSearch(false);
                            setInlineSearch('');
                          }}
                          disabled={addExerciseMutation.isPending}
                          className={cn(
                            'w-full text-left px-3 py-2 rounded-lg text-sm',
                            'text-gray-900 dark:text-white',
                            'transition-colors touch-manipulation',
                            'hover:bg-gray-100 dark:hover:bg-gray-700',
                            'active:bg-gray-200 dark:active:bg-gray-600',
                            'disabled:opacity-50'
                          )}
                        >
                          {ex.name}
                          {ex.muscleGroup && (
                            <span className="ml-2 text-xs text-gray-400">{ex.muscleGroup}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}

                {/* Avaa koko modaali */}
                <button
                  type="button"
                  onClick={() => {
                    setShowExercisePicker(true);
                    setShowInlineSearch(false);
                    setInlineSearch('');
                  }}
                  className={cn(
                    'w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium',
                    'text-gray-400 dark:text-gray-500',
                    'transition-colors touch-manipulation',
                    'hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  )}
                >
                  Selaa kaikkia liikkeitä
                </button>
              </div>
            )}
          </div>

          {/* Compact set table */}
          {currentExercise && (
            <CompactSetTable
              sets={currentExercise.sets}
              onDeleteSet={handleDeleteSet}
              isDeleting={deleteSetMutation.isPending}
              previousBestWeight={previousBestWeight}
            />
          )}

          {/* "Saved" feedback */}
          {setSavedFeedback && (
            <div className="flex items-center justify-center gap-2 py-2 text-green-600 dark:text-green-400 text-sm font-medium animate-fade-in">
              <Check className="h-4 w-4" />
              Sarja tallennettu!
            </div>
          )}

          {/* Lepoajastin — aikaleimapohjainen (osio 6.4) */}
          <RestTimer
            targetEnd={restTargetEnd}
            durationSeconds={effectiveRest}
            onComplete={() => setRestTargetEnd(null)}
            onSkip={() => setRestTargetEnd(null)}
            onAdjust={(delta) =>
              setRestTargetEnd((prev) =>
                prev == null ? prev : Math.max(Date.now(), prev + delta * 1000)
              )
            }
          />

          {/* New set input — always visible so it's clear you can log the next set */}
          {currentExercise && (
            <div className="card p-4 space-y-3">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Seuraava sarja: #{currentExercise.sets.length + 1}
                </p>
                {ghostText && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Edellinen: {ghostText}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <MobileNumberStepper
                  value={newSetWeight}
                  onChange={setNewSetWeight}
                  min={0}
                  max={500}
                  step={2.5}
                  label="Paino"
                  unit="kg"
                />
                <MobileNumberStepper
                  value={newSetReps}
                  onChange={(v) => setNewSetReps(v ?? 1)}
                  min={1}
                  max={100}
                  step={1}
                  label="Toistot"
                />
              </div>

              {/* Quick-adjust painonapit */}
              <div className="flex items-center justify-center gap-2">
                {[-5, -2.5, 2.5, 5].map((delta) => (
                  <button
                    key={delta}
                    type="button"
                    onClick={() => {
                      const current = newSetWeight ?? 0;
                      setNewSetWeight(Math.max(0, Math.round((current + delta) * 100) / 100));
                    }}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-semibold',
                      'transition-colors touch-manipulation active:scale-95',
                      delta > 0
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
                    )}
                  >
                    {delta > 0 ? '+' : ''}{delta} kg
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={handleConfirmSet}
                disabled={addSetMutation.isPending}
                className={cn(
                  'w-full flex items-center justify-center gap-2',
                  'min-h-[48px] px-4 rounded-xl font-semibold',
                  'bg-green-600 text-white',
                  'transition-all duration-200 touch-manipulation',
                  'hover:bg-green-700 active:scale-[0.98]',
                  'disabled:opacity-50 disabled:active:scale-100',
                  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-green-500'
                )}
              >
                {addSetMutation.isPending ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  <>
                    <Check className="h-5 w-5" />
                    Kirjaa sarja
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Exercise picker modal */}
      <ExercisePickerModal
        isOpen={showExercisePicker}
        onClose={() => setShowExercisePicker(false)}
        onSelect={handleSelectExercise}
        onCreateExercise={handleCreateExercise}
        exercises={allExercisesList}
        recentExerciseIds={recentExerciseIds}
        isCreating={createExerciseMutation.isPending}
      />
    </div>
  );
}
